import { useEffect, useState } from 'react';
import type { RepresentDocument } from './representData';
import logoHeader from '@/assets/logo_header.png';

interface PdfReaderModalProps {
  document: RepresentDocument | null;
  allDocuments: RepresentDocument[];
  onClose: () => void;
  onSelectDocument: (doc: RepresentDocument) => void;
}

export const PdfReaderModal = ({
  document,
  allDocuments,
  onClose,
  onSelectDocument,
}: PdfReaderModalProps) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [document?.id]);

  useEffect(() => {
    if (!document) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        const currentIndex = allDocuments.findIndex((d) => d.id === document.id);
        if (currentIndex < allDocuments.length - 1) {
          onSelectDocument(allDocuments[currentIndex + 1]);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        const currentIndex = allDocuments.findIndex((d) => d.id === document.id);
        if (currentIndex > 0) {
          onSelectDocument(allDocuments[currentIndex - 1]);
        }
      }
    };

    const originalOverflow = window.getComputedStyle(window.document.body).overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [document, allDocuments, onClose, onSelectDocument]);

  if (!document) return null;

  const currentIndex = allDocuments.findIndex((d) => d.id === document.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allDocuments.length - 1;

  const categoryColorClass = {
    representation: 'bg-emerald-50 text-emerald-800 border-emerald-200/80',
    whitepaper: 'bg-purple-50 text-purple-800 border-purple-200/80',
    annexure: 'bg-amber-50 text-amber-900 border-amber-200/80',
  }[document.category];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-950/80 backdrop-blur-md animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-modal-title"
    >
      <div
        className="relative w-full max-w-6xl h-[94vh] max-h-[920px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-fadeInUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Header */}
        <div className="p-3 sm:p-4 bg-surface border-b border-gray-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          <div className="flex items-start sm:items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-bteal/10 text-bteal flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
              <i className="ri-file-pdf-2-line text-xl text-red-500" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${categoryColorClass}`}>
                  {document.categoryLabel}
                </span>
                <span className="text-xs font-semibold text-ink-muted flex items-center gap-1">
                  <i className="ri-calendar-line text-gray-400" />
                  {document.date}
                </span>
                {document.referenceNo && (
                  <span className="hidden md:inline-block text-[11px] font-mono px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {document.referenceNo}
                  </span>
                )}
                <span className="text-[11px] font-semibold text-bteal bg-bteal/10 px-2 py-0.5 rounded">
                  {document.pageCount}
                </span>
              </div>
              <h2
                id="pdf-modal-title"
                className="text-sm sm:text-base md:text-lg font-bold text-ink truncate leading-tight"
                title={document.title}
              >
                {document.title}
              </h2>
            </div>
          </div>

          {/* Modal Action Controls */}
          <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-100">
            {/* Pagination Controls */}
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 shadow-2xs">
              <button
                onClick={() => hasPrev && onSelectDocument(allDocuments[currentIndex - 1])}
                disabled={!hasPrev}
                className="w-7 h-7 flex items-center justify-center rounded text-ink hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Previous Document (Left Arrow)"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <span className="text-xs font-semibold text-ink-muted px-1.5 whitespace-nowrap">
                {currentIndex + 1} / {allDocuments.length}
              </span>
              <button
                onClick={() => hasNext && onSelectDocument(allDocuments[currentIndex + 1])}
                disabled={!hasNext}
                className="w-7 h-7 flex items-center justify-center rounded text-ink hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Next Document (Right Arrow)"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>

            {/* Open in New Window */}
            <a
              href={document.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-ink text-xs font-semibold hover:border-bteal hover:text-bteal hover:bg-bteal/5 transition-colors cursor-pointer shadow-2xs"
              title="Open PDF in new tab"
            >
              <i className="ri-external-link-line text-sm" />
              <span className="hidden md:inline">Open New Tab</span>
            </a>

            {/* Direct Download */}
            <a
              href={document.pdfUrl}
              download={`${document.title}.pdf`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-colors cursor-pointer shadow-2xs"
              title="Download PDF copy"
            >
              <i className="ri-download-2-line text-sm" />
              <span className="hidden md:inline">Download</span>
            </a>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-600 text-gray-500 flex items-center justify-center transition-colors cursor-pointer ml-1"
              title="Close Reader (Esc)"
              aria-label="Close reader"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Sub-bar Information Note */}
        <div className="bg-slate-50 px-4 py-2 border-b border-gray-200/70 text-xs text-ink-muted flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 truncate">
            <span className="font-semibold text-ink flex items-center gap-1 shrink-0">
              <i className="ri-building-line text-bteal" />
              Authority / Body:
            </span>
            <span className="truncate">{document.authority}</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 shrink-0 text-[11px]">
            <span className="flex items-center gap-1 text-gray-400">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">Esc</kbd>
              to close
            </span>
            <span className="flex items-center gap-1 text-gray-400">
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">&larr;</kbd>
              <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">&rarr;</kbd>
              to navigate
            </span>
          </div>
        </div>

        {/* PDF Reader Canvas */}
        <div className="relative flex-1 w-full bg-slate-900/5 p-2 sm:p-3 overflow-hidden">
          {!iframeLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 z-10 transition-opacity">
              <div className="w-10 h-10 border-3 border-bteal border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-xs font-semibold text-ink">Loading PDF document...</p>
              <p className="text-[11px] text-ink-muted mt-1">{document.title}</p>
            </div>
          )}
          <iframe
            key={document.id}
            src={`${document.pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
            className="w-full h-full rounded-xl border border-gray-200 bg-white shadow-inner"
            title={document.title}
            onLoad={() => setIframeLoaded(true)}
          />
        </div>

        {/* Modal Bottom Footer */}
        <div className="p-3 sm:p-4 bg-surface border-t border-gray-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <img src={logoHeader} alt="BEA" className="h-7 w-auto object-contain hidden sm:inline-block" />
            <p className="text-xs text-ink-muted leading-relaxed line-clamp-1 max-w-2xl">
              <span className="font-semibold text-ink">Summary: </span>
              {document.summary}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-ink text-xs font-semibold hover:bg-gray-50 transition-colors shadow-2xs cursor-pointer"
            >
              Close Reader
            </button>
            <a
              href={document.pdfUrl}
              download={`${document.title}.pdf`}
              className="px-4 py-2 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-colors shadow-2xs cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-download-line text-xs" />
              <span>Download File ({document.fileSize})</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
